<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center justify-space-between">
            <span>Gerenciamento de Letreiros</span>
            <v-btn color="primary" @click="openLetreiroDialog()">
              <v-icon left>mdi-plus</v-icon>
              Novo Letreiro
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col v-for="(letreiro, index) in paginatedLetreiros"
                :key="`letreiro-${index + (page - 1) * itemsPerPage}`" cols="12" md="6" lg="4">
                <v-card class="mb-4 hover-card" outlined hover>
                  <v-card-title class="pb-2">
                    <span class="text-h6 flex-grow-1 text-truncate">
                      {{ letreiro.name || 'Letreiro sem nome' }}
                    </span>
                    
                      <v-menu  open-delay="200" left open-on-hover>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon v-bind="attrs" v-on="on">
                            <v-icon>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>
                        <v-list>
                          <v-list-item @click="openLetreiroDialog(index)">
                            <v-list-item-icon><v-icon>mdi-pencil</v-icon></v-list-item-icon>
                            <v-list-item-title>Editar</v-list-item-title>
                          </v-list-item>
                          <v-list-item @click="confirmDeleteLetreiro(index)">
                            <v-list-item-icon><v-icon>mdi-delete</v-icon></v-list-item-icon>
                            <v-list-item-title>Excluir</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                  </v-card-title>
                  <v-card-text class="pt-0">
                    <div class="text-body-2 mb-2">
                      <strong>Texto:</strong> {{ truncateText(letreiro.text) }}
                    </div>

                    <!-- Pré-visualização do Letreiro -->
                    <div class="mb-3">
                      <Letreiro :text="letreiro.text"
                        :key="`letreiro-${index + (page - 1) * itemsPerPage}-${letreiro.text}`" />
                    </div>

                    <div class="text-caption text--secondary mb-3">
                      Criado em: {{ formatDate(letreiro.createdAt) }} | Posição: {{ letreiro.position }}
                    </div>
                    <div class="text-caption text--secondary">
                      <strong>Fontes ({{ (letreiro.fonts || []).length }}):</strong>
                      <span v-if="letreiro.fonts && letreiro.fonts.length">
                        {{ letreiro.fonts.map(truncateUrl).join(', ') }}
                      </span>
                      <span v-else>Nenhuma fonte</span>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Paginação -->
            <div class="d-flex justify-center mt-6" v-if="totalPages > 1">
              <v-pagination v-model="page" :length="totalPages" :total-visible="7" color="primary"></v-pagination>
            </div>

            <div class="d-flex align-center justify-space-between mt-4">
              <div class="text-body-2 text--secondary">
                Mostrando {{ startItem }}-{{ endItem }} de {{ totalItems }} letreiros
              </div>
              <v-select v-model="itemsPerPage" :items="[6, 12, 24, 48]" label="Itens por página" dense outlined
                style="max-width: 150px" @change="page = 1"></v-select>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog para editar letreiro -->
    <v-dialog v-model="letreiroDialog" max-width="600px">
      <v-card>
        <v-card-title>
          {{ editingLetreiroIndex !== null ? 'Editar Letreiro' : 'Novo Letreiro' }}
        </v-card-title>
        <v-card-text>
          <v-text-field v-model="currentLetreiro.name" hide-details label="Nome do Letreiro" outlined
            class="mb-2"></v-text-field>
          <div class="mb-6">
            <div class="text-subtitle-2 mb-1">Fontes ({{ currentLetreiro.fonts.length }})</div>
            <v-chip v-for="(font, fontIndex) in currentLetreiro.fonts" :key="fontIndex" class="ma-1" close
              @click:close="confirmDeleteFont(currentLetreiroIndex, fontIndex)">
              {{ truncateUrl(font) }}
            </v-chip>
            <v-btn text small color="primary" @click="openFontDialog(currentLetreiroIndex)" class="mt-1">
              <v-icon left small>mdi-plus</v-icon>
              Adicionar Fonte
            </v-btn>
          </div>
          <v-textarea v-model="currentLetreiro.text" label="Texto do Letreiro" outlined hide-details rows="3"
            class="mb-4"></v-textarea>
          <v-select v-model="currentLetreiro.position" :items="['TOPO', 'RODAPÉ']" label="Posição" outlined hide-details
            class="mb-4"></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="letreiroDialog = false">Cancelar</v-btn>
          <v-btn color="primary" @click="saveLetreiro">Salvar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para adicionar fonte -->
    <v-dialog v-model="fontDialog" max-width="500px">
      <v-card>
        <v-card-title>Adicionar Fonte RSS</v-card-title>
        <v-card-text>
          <v-text-field v-model="newFontUrl" label="URL da Fonte RSS" outlined placeholder="https://exemplo.com/rss"
            :rules="urlRules"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="fontDialog = false">Cancelar</v-btn>
          <v-btn color="primary" @click="addFont" :disabled="!newFontUrl">Adicionar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de confirmação -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h6">Confirmação</v-card-title>
        <v-card-text>{{ deleteMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="error" @click="executeDelete">Excluir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Loading -->
    <v-dialog v-model="loading" persistent fullscreen>
      <v-container fill-height class="d-flex align-center justify-center">
        <v-card class="pa-8 text-center" elevation="10">
          <v-progress-circular indeterminate color="primary" size="80" width="8" class="mb-4"></v-progress-circular>
          <div class="text-h6 mb-2">Processando...</div>
          <div class="text-body-1 text--secondary">Aguarde enquanto processamos sua solicitação</div>
        </v-card>
      </v-container>
    </v-dialog>
  </v-container>
</template>

<script>
import Letreiro from './Letreiro.vue'
import { listLetreiros, createLetreiro, updateLetreiro, deleteLetreiro } from '@/services/letreiros'
import { mapState } from 'vuex'

export default {
  name: 'LetreirosList',
  components: {
    Letreiro
  },

  data() {
    return {
      letreiros: [],
      letreiroDialog: false,
      fontDialog: false,
      deleteDialog: false,
      editingLetreiroIndex: null,
      currentLetreiroIndex: null,
      currentLetreiro: {
        name: '',
        text: '',
        position: 'TOPO',
        fonts: []
      },
      newFontUrl: '',
      deleteMessage: '',
      deleteAction: null,
      // Paginação
      page: 1,
      itemsPerPage: 12,
      loading: false,
      urlRules: [
        v => !!v || 'URL é obrigatória',
        v => /^https?:\/\/.+/.test(v) || 'URL deve começar com http:// ou https://'
      ]
    };
  },
  computed: {
    ...mapState('letreiros', ['data', 'total']),
    paginatedLetreiros() {
      if (!this.data || !Array.isArray(this.data)) return [];
      return this.data;
    },
    totalPages() {
      return Math.max(1, Math.ceil((this.total || 0) / this.itemsPerPage));
    },
    totalItems() {
      return this.total || 0;
    },
    startItem() {
      if (!this.total) return 0;
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    endItem() {
      if (!this.total) return 0;
      const end = this.page * this.itemsPerPage;
      return Math.min(end, this.total);
    }
  },
  async mounted() {
    await this.loadLetreiros()
  },
  methods: {
    async loadLetreiros() {
      try {
        this.loading = true
        await listLetreiros({ page: this.page, limit: this.itemsPerPage })
      } catch (error) {
        this.$store.commit('failed', error)
      } finally {
        this.loading = false
      }
    },
    truncateText(text) {
      return text && text.length > 50 ? text.substring(0, 50) + '...' : text;
    },
    truncateUrl(url) {
      return url && url.length > 30 ? url.substring(0, 30) + '...' : url;
    },
    formatDate(dateString) {
      if (!dateString) return 'Data não disponível';
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR');
    },
    openLetreiroDialog(index = null) {
      this.editingLetreiroIndex = index
      this.currentLetreiroIndex = index
      if (index !== null && this.paginatedLetreiros && this.paginatedLetreiros[index]) {
        const letreiro = this.paginatedLetreiros[index]
        this.currentLetreiro = { ...letreiro, fonts: letreiro.fonts || [] }
      } else {
        this.currentLetreiro = { name: '', text: '', position: 'TOPO', fonts: [] }
      }
      this.letreiroDialog = true
    },
    async saveLetreiro() {
      try {
        this.loading = true
        if (this.editingLetreiroIndex !== null && this.data && Array.isArray(this.data)) {
          // Editar letreiro existente
          const letreiroId = this.data[this.editingLetreiroIndex]?._id
          if (letreiroId) {
            const updatedLetreiro = await updateLetreiro(letreiroId, this.currentLetreiro)
            this.$store.commit('letreiros/updateLetreiro', updatedLetreiro)
          }
        } else {
          // Criar novo letreiro
          const newLetreiro = await createLetreiro(this.currentLetreiro)
          this.$store.commit('letreiros/addLetreiro', newLetreiro)
        }
        this.letreiroDialog = false
      } catch (error) {
        this.$store.commit('failed', error)
      } finally {
        this.loading = false
      }
    },
    openFontDialog(letreiroIndex) {
      this.currentLetreiroIndex = letreiroIndex;
      this.newFontUrl = '';
      this.fontDialog = true;
    },
    async addFont() {
      if (!this.newFontUrl) return
      this.currentLetreiro.fonts = [...(this.currentLetreiro.fonts || []), this.newFontUrl]
      this.fontDialog = false
      this.newFontUrl = ''
    },
    confirmDeleteLetreiro(index) {
      if (this.paginatedLetreiros && this.paginatedLetreiros[index]) {
        const letreiro = this.paginatedLetreiros[index]
        this.deleteMessage = `Tem certeza que deseja excluir o letreiro "${letreiro.name}"?`
        this.deleteAction = () => this.deleteLetreiro(letreiro._id)
        this.deleteDialog = true
      }
    },
    confirmDeleteFont(letreiroIndex, fontIndex) {
      this.deleteMessage = 'Tem certeza que deseja remover esta fonte RSS?';
      this.deleteAction = () => this.deleteFont(letreiroIndex, fontIndex);
      this.deleteDialog = true;
    },
    executeDelete() {
      if (this.deleteAction) {
        this.deleteAction();
        this.deleteAction = null;
      }
      this.deleteDialog = false;
    },
    async deleteLetreiro(id) {
      try {
        this.loading = true
        await deleteLetreiro(id)
        this.$store.commit('letreiros/removeLetreiro', id)
      } catch (error) {
        this.$store.commit('setAlert1', {
          message: error,
          color: 'error',
          icon: 'mdi-alert-circle'
        })
      } finally {
        this.loading = false
      }
    },
    async deleteFont(letreiroIndex, fontIndex) {
      if (this.currentLetreiro && Array.isArray(this.currentLetreiro.fonts)) {
        this.currentLetreiro.fonts.splice(fontIndex, 1)
      }
    },
  },
  watch: {
    page() {
      this.loadLetreiros()
    },
    itemsPerPage() {
      this.page = 1
      this.loadLetreiros()
    }
  }
};
</script>

<style scoped>
.hover-card:hover {
  background-color: var(--v-background-base) !important;
  border-color: var(--v-primary-base) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  transition: all 0.3s ease;
}

.theme--dark .hover-card:hover {
  background-color: rgba(255, 255, 255, 0.05) !important;
  border-color: var(--v-primary-base) !important;
}

.theme--light .hover-card:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
  border-color: var(--v-primary-base) !important;
}
</style>
